import React, { useEffect, useState, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getNextParsing } from "../../services/TimerService";
import { getLatestSeason } from "../../services/MatchService";
import { getFeaturedClips } from "../../services/HighlightsService";
import SiteBanner from "../../components/SiteBanner/SiteBanner";
import LiveEventStream from "../../components/LiveEventStream/LiveEventStream";
import { HighlightClip } from "../../models/HighlightClip";
import RecentMatches from "../../components/RecentMatches";
import { useLiveMatches } from "../../services/LiveService";
import './HomePageV2.css';
import '../../styles/theme.css';

import inferno from '../../content/maps/de_inferno.jpg';
import mirage from '../../content/maps/de_mirage.jpg';
import nuke from '../../content/maps/de_nuke.jpg';
import overpass from '../../content/maps/de_overpass.jpg';
import ancient from '../../content/maps/de_ancient.jpg';
import vertigo from '../../content/maps/de_vertigo.jpg';
import anubis from '../../content/maps/de_anubis.jpg';
import dust2 from '../../content/maps/de_dust2.jpg';
import train from '../../content/maps/de_train.jpg';
import cache from '../../content/maps/de_cache.jpg';

const VOLUME_STORAGE_KEY = 'powerstats-clip-volume';
const MUTED_STORAGE_KEY = 'powerstats-clip-muted';

const mapImages: Record<string, string> = {
    'de_inferno': inferno, 'de_mirage': mirage, 'de_nuke': nuke, 'de_overpass': overpass,
    'de_vertigo': vertigo, 'de_ancient': ancient, 'de_anubis': anubis, 'de_dust2': dust2, 'de_train': train, 'de_cache': cache,
    'inferno': inferno, 'mirage': mirage, 'nuke': nuke, 'overpass': overpass,
    'vertigo': vertigo, 'ancient': ancient, 'anubis': anubis, 'dust2': dust2, 'train': train, 'cache': cache
};

const getMapImage = (mapName?: string | null) => mapImages[mapName?.toLowerCase() || ''] || dust2;

const formatMapName = (mapName: string | undefined | null): string => {
    if (!mapName) return 'Unknown';
    const cleanName = mapName.replace('de_', '');
    return cleanName.charAt(0).toUpperCase() + cleanName.slice(1);
};

const isClutchType = (type: string | undefined): boolean => {
    if (!type) return false;
    return type.toLowerCase().startsWith('clutch');
};

const getClutchLabel = (type: string): string => {
    const lower = type.toLowerCase();
    if (lower === 'clutch1v2') return '1v2';
    if (lower === 'clutch1v3') return '1v3';
    if (lower === 'clutch1v4') return '1v4';
    if (lower === 'clutch1v5') return '1v5';
    if (lower === 'clutch2v4') return '2v4';
    if (lower === 'clutch2v5') return '2v5';
    return 'Clutch';
};

const KillBadge: React.FC<{ kills: number; highlightType?: string }> = ({ kills, highlightType }) => {
    const isClutch = isClutchType(highlightType);
    const is2vX = highlightType?.toLowerCase().startsWith('clutch2v');

    const getBadgeClass = () => {
        if (is2vX) return 'kill-badge clutch-2vx';
        if (isClutch) return 'kill-badge clutch';
        if (kills >= 5) return 'kill-badge ace';
        if (kills >= 4) return 'kill-badge quad';
        if (kills >= 3) return 'kill-badge triple';
        return 'kill-badge';
    };

    const getLabel = () => {
        if (isClutch && highlightType) return getClutchLabel(highlightType);
        if (kills >= 5) return 'ACE';
        return `${kills}K`;
    };

    return <span className={getBadgeClass()}>{getLabel()}</span>;
};

const HomePageV2: React.FC = () => {
    const { t } = useTranslation(['pages', 'common']);
    // Results that are in but not parsed yet, as on the Kampe page's Resultater
    const { finished } = useLiveMatches();

    const [nextUpdate, setNextUpdate] = useState<Date | null>(null);
    const [countdown, setCountdown] = useState<string>("");
    const [topClips, setTopClips] = useState<HighlightClip[]>([]);
    const [clipLabel, setClipLabel] = useState<string>("season");
    const [clipSeason, setClipSeason] = useState<number | undefined>(undefined);
    const [clipsLoading, setClipsLoading] = useState(true);
    const [currentSeason, setCurrentSeason] = useState<number | null>(null);
    const [activeIndex, setActiveIndex] = useState(0);
    const [autoAdvances, setAutoAdvances] = useState(0);

    const videoRef = useRef<HTMLVideoElement>(null);

    const MAX_AUTO_ADVANCES = 10;


    // Fetch next parsing time
    useEffect(() => {
        const timeZoneId = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const fetchNextParsing = async () => {
            try {
                const nextTimeUtc = await getNextParsing(timeZoneId);
                if (nextTimeUtc) {
                    setNextUpdate(new Date(nextTimeUtc));
                }
            } catch (error) {
                console.error("Failed to fetch the next parsing time:", error);
            }
        };
        fetchNextParsing();
    }, []);

    // Countdown interval
    useEffect(() => {
        if (!nextUpdate) return;
        let interval: ReturnType<typeof setInterval> | null = null;
        const tick = () => {
            const timeDifference = nextUpdate.getTime() - Date.now();
            if (timeDifference <= 0) {
                if (interval) clearInterval(interval);
                setCountdown(t('pages:homeV2.updatingNow'));
                return;
            }
            const days = Math.floor(timeDifference / (1000 * 60 * 60 * 24));
            const hours = Math.floor((timeDifference / (1000 * 60 * 60)) % 24);
            const minutes = Math.floor((timeDifference / (1000 * 60)) % 60);
            const seconds = Math.floor((timeDifference / 1000) % 60);
            setCountdown(
                `${days > 0 ? days + " " + t('common:time.days') + ", " : ""}${hours} ${t('common:time.hours')}, ${minutes} ${t('common:time.minutes')}, ${seconds} ${t('common:time.seconds')}`
            );
        };
        tick();
        interval = setInterval(tick, 1000);
        return () => { if (interval) clearInterval(interval); };
    }, [nextUpdate, t]);

    // Fetch featured clips + current season
    useEffect(() => {
        const fetchClips = async () => {
            setClipsLoading(true);
            try {
                const latestSeason = await getLatestSeason();
                setCurrentSeason(latestSeason);
                const response = await getFeaturedClips(latestSeason);
                setTopClips(response.clips);
                setClipLabel(response.label);
                setClipSeason(response.season);
            } catch (error) {
                console.error("Failed to fetch featured clips:", error);
            } finally {
                setClipsLoading(false);
            }
        };
        fetchClips();
    }, []);

    // Pause video when tab is hidden; resume when visible again
    useEffect(() => {
        const onVisibilityChange = () => {
            if (!videoRef.current) return;
            if (document.hidden) {
                videoRef.current.pause();
            } else {
                videoRef.current.play().catch(() => {});
            }
        };
        document.addEventListener('visibilitychange', onVisibilityChange);
        return () => document.removeEventListener('visibilitychange', onVisibilityChange);
    }, []);

    // Advance to next clip when current video ends — capped to avoid looping forever on AFK tabs
    const handleVideoEnded = useCallback(() => {
        if (topClips.length > 1 && autoAdvances < MAX_AUTO_ADVANCES) {
            setAutoAdvances(n => n + 1);
            setActiveIndex(prev => (prev + 1) % topClips.length);
        }
    }, [topClips.length, autoAdvances]);

    const goTo = (index: number) => {
        setAutoAdvances(0);
        setActiveIndex(index);
    };

    // Autoplay video when active slide changes
    useEffect(() => {
        const timeout = setTimeout(() => {
            if (videoRef.current) {
                videoRef.current.muted = true;
                videoRef.current.play().catch(() => {});
            }
        }, 100);
        return () => clearTimeout(timeout);
    }, [activeIndex]);

    const goPrev = () => goTo((activeIndex - 1 + topClips.length) % topClips.length);
    const goNext = () => goTo((activeIndex + 1) % topClips.length);

    // Volume persistence
    // Always start muted on homepage — restore volume level but force muted, then play
    const handleVideoLoaded = useCallback(() => {
        if (videoRef.current) {
            const savedVol = localStorage.getItem(VOLUME_STORAGE_KEY);
            videoRef.current.volume = savedVol !== null ? parseFloat(savedVol) : 1;
            videoRef.current.muted = true;
            videoRef.current.play().catch(() => {});
        }
    }, []);

    const handleVolumeChange = useCallback(() => {
        if (videoRef.current) {
            localStorage.setItem(VOLUME_STORAGE_KEY, videoRef.current.volume.toString());
            // Don't persist muted state from homepage — let /clips page manage its own
        }
    }, []);

    // Position helper: -1 = left, 0 = center, 1 = right
    const getCardPosition = (index: number): number => {
        const diff = index - activeIndex;
        if (diff === 0) return 0;
        if (diff === 1 || diff === -(topClips.length - 1)) return 1;
        if (diff === -1 || diff === (topClips.length - 1)) return -1;
        return 2; // hidden
    };

    return (
        <div className="home-v2">
            <SiteBanner />

            {/* 72 Games på 72 timer: Twitch player while a channel is live (renders nothing otherwise) */}
            <LiveEventStream />

            {/* Welcome Section */}
            <section className="home-v2-welcome">
                <h1>{t('pages:home.welcome')}</h1>
                <p className="home-v2-welcome-subtitle">
                    {t('pages:homeV2.descriptionDynamic', { season: currentSeason || '...' })}
                </p>

                <div className="home-v2-cta">
                    <a href="https://qr.mobilepay.dk/box/9229be2b-bcf7-4f26-acfc-e43ed97e7a5d/pay-in" target="_blank" rel="noreferrer" className="home-v2-cta-btn">
                        {t('pages:home.supportMobilePay')}
                    </a>
                    <a href="https://discord.gg/W54Zhj2PD3" target="_blank" rel="noreferrer" className="home-v2-cta-btn home-v2-cta-discord">
                        {t('pages:home.joinDiscord')}
                    </a>
                </div>

                <div className="home-v2-timer-pill">
                    <span className="timer-label">{t('pages:homeV2.nextUpdate')}</span>
                    <span className="timer-value">{countdown || '—'}</span>
                </div>
            </section>

            {/* Clip Carousel — wrapper always rendered while loading or with content
                so the section reserves vertical space and doesn't shove content downward. */}
            {(clipsLoading || topClips.length > 0) && (
                <section className="home-v2-carousel-wrapper">
                    {clipsLoading && (
                        <div className="home-v2-carousel-skeleton" aria-hidden="true" />
                    )}
                    {!clipsLoading && topClips.length > 0 && (<>
                    <div className="home-v2-carousel">
                        {topClips.map((clip, index) => {
                            const pos = getCardPosition(index);
                            const isCenter = pos === 0;
                            const posClass = pos === 0 ? 'center' : pos === -1 ? 'left' : pos === 1 ? 'right' : 'hidden';

                            return (
                                <div
                                    key={clip.id}
                                    className={`home-v2-carousel-card ${posClass}`}
                                    onClick={() => !isCenter && goTo(index)}
                                >
                                    {isCenter ? (
                                        /* Center card: video + info */
                                        <div className="home-v2-carousel-center">
                                            <div className="home-v2-carousel-video">
                                                {clip.clipUrl ? (
                                                    <video
                                                        ref={videoRef}
                                                        key={clip.id}
                                                        controls
                                                        autoPlay
                                                        muted
                                                        playsInline
                                                        src={clip.clipUrl}
                                                        poster={clip.thumbnailUrl || getMapImage(clip.mapName)}
                                                        onLoadedData={handleVideoLoaded}
                                                        onVolumeChange={handleVolumeChange}
                                                        onEnded={handleVideoEnded}
                                                    />
                                                ) : (
                                                    <img src={clip.thumbnailUrl || getMapImage(clip.mapName)} alt="" />
                                                )}
                                            </div>
                                            <div className="home-v2-carousel-info">
                                                <span className="home-v2-carousel-watch-badge">WATCH</span>
                                                <span className="home-v2-carousel-title">
                                                    {clipLabel === 'day' ? t('pages:homeV2.clipOfTheDay') :
                                                     clipLabel === 'week' ? t('pages:homeV2.clipOfTheWeek') :
                                                     clipLabel === 'previousSeason' && clipSeason
                                                        ? t('pages:homeV2.topClipOfSeason', { season: clipSeason })
                                                     : t('pages:homeV2.topClip')}
                                                </span>
                                                <div className="home-v2-carousel-player-row">
                                                    <KillBadge kills={clip.killCount} highlightType={clip.highlightType} />
                                                    <span className="home-v2-carousel-player">{clip.playerName || 'Unknown'}</span>
                                                </div>
                                                <span className="home-v2-carousel-meta">{formatMapName(clip.mapName)} &middot; Runde {clip.roundNr}</span>
                                                <Link to="/clips" className="home-v2-carousel-btn">
                                                    {t('pages:homeV2.watchClip')}
                                                </Link>
                                            </div>
                                        </div>
                                    ) : (
                                        /* Side card: thumbnail + overlay */
                                        <div className="home-v2-carousel-side">
                                            <img src={clip.thumbnailUrl || getMapImage(clip.mapName)} alt="" className={!clip.thumbnailUrl ? 'map-fallback' : ''} />
                                            <div className="home-v2-carousel-side-overlay">
                                                <span className="home-v2-carousel-side-badge">WATCH</span>
                                                <span className="home-v2-carousel-side-player">{clip.playerName || 'Unknown'}</span>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>

                    {/* Arrows */}
                    {topClips.length > 1 && (
                        <>
                            <button className="home-v2-carousel-arrow left" onClick={goPrev} aria-label="Previous">
                                <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20"><path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z"/></svg>
                            </button>
                            <button className="home-v2-carousel-arrow right" onClick={goNext} aria-label="Next">
                                <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20"><path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z"/></svg>
                            </button>
                        </>
                    )}

                    {/* Dots */}
                    {topClips.length > 1 && (
                        <div className="home-v2-carousel-dots">
                            {topClips.map((_, i) => (
                                <button
                                    key={i}
                                    className={`home-v2-carousel-dot ${i === activeIndex ? 'active' : ''}`}
                                    onClick={() => goTo(i)}
                                    aria-label={`Slide ${i + 1}`}
                                />
                            ))}
                        </div>
                    )}

                    <div className="home-v2-carousel-browse">
                        <Link to="/clips" className="home-v2-carousel-browse-btn">
                            {t('pages:homeV2.seeAllClips')} &rarr;
                        </Link>
                    </div>
                    </>)}
                </section>
            )}

            {/* Recent Matches */}
            <section className="home-v2-section home-v2-section-matches">
                <div className="home-v2-section-header">
                    <h2>{t('pages:homeV2.latestResults')}</h2>
                </div>
                <RecentMatches compact={false} pending={finished} />
            </section>
        </div>
    );
};

export default HomePageV2;
